import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import {
    Badge,
    Box,
    BreadcrumbGroup,
    Button,
    Container,
    Header,
    Input,
    Link,
    SpaceBetween,
    StatusIndicator,
    TextFilter,
} from "@cloudscape-design/components";
import { getLeadershipRiskSummary } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { GetLeadershipRiskSummaryResponse, LeadershipRiskSummary } from "src/core/sentinel/types";

interface ManagerNode {
    loading: boolean;
    error: string;
    manager: LeadershipRiskSummary | null;
    directReports: LeadershipRiskSummary[];
}

// Guards the parentAlias walk against a cycle in the org data.
const MAX_ANCESTOR_DEPTH = 15;

const SELECTED_BACKGROUND = "#f2f8fd";
const SELECTED_BORDER = "#0972d3";
const CARD_BORDER = "#e9ebed";

const sortReports = (reports: LeadershipRiskSummary[]) =>
    [...reports].sort((a, b) =>
        b.highRiskCount - a.highRiskCount ||
        b.atRiskCount - a.atRiskCount ||
        a.managerName.localeCompare(b.managerName),
    );

const RiskBadges: React.FC<{ summary: LeadershipRiskSummary }> = ({ summary }) => (
    <SpaceBetween direction="horizontal" size="xxs">
        <Badge color={summary.highRiskCount > 0 ? "red" : "grey"}>
            High risk: {summary.highRiskCount}
        </Badge>
        <Badge color="grey">Packages: {summary.totalPackages}</Badge>
    </SpaceBetween>
);

interface LeadershipTreeProps {
    sentinelController: SentinelController;
    selectedAlias: string;
    selectedSummary: GetLeadershipRiskSummaryResponse | null;
    selectedLoading: boolean;
    onSelect: (alias: string) => void;
}

export const LeadershipTree: React.FC<LeadershipTreeProps> = (props: LeadershipTreeProps) => {
    const dispatch = useDispatch();
    const [nodes, setNodes] = useState<Record<string, ManagerNode>>({});
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [ancestors, setAncestors] = useState<LeadershipRiskSummary[]>([]);
    const [filteringText, setFilteringText] = useState("");
    const [aliasInput, setAliasInput] = useState(props.selectedAlias);

    const storeNode = (alias: string, node: ManagerNode) =>
        setNodes((prev) => ({ ...prev, [alias]: node }));

    // Fetches (or returns the cached) summary for one manager.
    const loadNode = async (alias: string): Promise<ManagerNode> => {
        const cached = nodesRef.current[alias];
        if (cached && !cached.loading && !cached.error) return cached;
        storeNode(alias, { loading: true, error: "", manager: null, directReports: [] });
        try {
            const response: any = await dispatch(
                getLeadershipRiskSummary({ sentinelController: props.sentinelController, managerAlias: alias }),
            ).unwrap();
            const summary = response?.payload as GetLeadershipRiskSummaryResponse;
            const node = {
                loading: false,
                error: "",
                manager: summary?.manager ?? null,
                directReports: summary?.directReports ?? [],
            };
            storeNode(alias, node);
            return node;
        } catch (e) {
            const node = {
                loading: false,
                error: `Failed to load direct reports for ${alias}.`,
                manager: null,
                directReports: [],
            };
            storeNode(alias, node);
            return node;
        }
    };

    // Reuse the page's fetch for the selected manager.
    useEffect(() => {
        if (props.selectedLoading || !props.selectedSummary) return;
        storeNode(props.selectedAlias, {
            loading: false,
            error: "",
            manager: props.selectedSummary.manager ?? null,
            directReports: props.selectedSummary.directReports ?? [],
        });
    }, [props.selectedSummary, props.selectedLoading]);

    useEffect(() => {
        setAliasInput(props.selectedAlias);
        setFilteringText("");
        setExpanded({});
    }, [props.selectedAlias]);

    const selectedManager = nodes[props.selectedAlias]?.manager ?? null;
    useEffect(() => {
        let cancelled = false;
        const walk = async () => {
            const chain: LeadershipRiskSummary[] = [];
            const seen = new Set<string>([props.selectedAlias]);
            let parent = selectedManager?.parentAlias;
            while (parent && !seen.has(parent) && chain.length < MAX_ANCESTOR_DEPTH) {
                seen.add(parent);
                const node = await loadNode(parent);
                if (!node.manager) break;
                chain.unshift(node.manager);
                parent = node.manager.parentAlias;
            }
            if (!cancelled) setAncestors(chain);
        };
        setAncestors([]);
        if (selectedManager) walk();
        return () => {
            cancelled = true;
        };
    }, [selectedManager?.managerAlias]);

    const toggle = (alias: string) => {
        const expanding = !expanded[alias];
        setExpanded((prev) => ({ ...prev, [alias]: expanding }));
        if (expanding) loadNode(alias);
    };

    const goToAlias = (target: string) => {
        if (target && target.trim()) props.onSelect(target.trim());
    };

    const renderReports = (reports: LeadershipRiskSummary[], depth: number): React.ReactNode =>
        sortReports(reports).map((report) => renderReport(report, depth));

    const renderReport = (report: LeadershipRiskSummary, depth: number): React.ReactNode => {
        const alias = report.managerAlias;
        const node = nodes[alias];
        const knownLeaf = !!node && !node.loading && !node.error && node.directReports.length === 0;
        const isExpanded = !!expanded[alias];
        return (
            <div key={`${depth}/${alias}`} style={{ marginLeft: depth * 20 }}>
                <div
                    // The whole card opens the manager. Clicks on the caret or the
                    // name link are handled by those controls, so skip them here.
                    // The name link stays the keyboard target, so the card itself
                    // isn't a second tab stop.
                    onClick={(event) => {
                        if ((event.target as HTMLElement).closest("a, button")) return;
                        goToAlias(alias);
                    }}
                    style={{
                        border: `1px solid ${CARD_BORDER}`,
                        borderRadius: 8,
                        padding: "8px 12px",
                        marginBottom: 8,
                        cursor: "pointer",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ width: 20, flexShrink: 0 }}>
                            {!knownLeaf && (
                                <Button
                                    variant="inline-icon"
                                    iconName={isExpanded ? "caret-down-filled" : "caret-right-filled"}
                                    ariaLabel={`Direct reports of ${report.managerName}`}
                                    ariaExpanded={isExpanded}
                                    onClick={() => toggle(alias)}
                                />
                            )}
                        </div>
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <SpaceBetween size="xxs">
                                <div>
                                    <Link
                                        href={`#/leadership-dashboard/${encodeURIComponent(alias)}`}
                                        onFollow={(event) => {
                                            event.preventDefault();
                                            goToAlias(alias);
                                        }}
                                    >
                                        {report.managerName}
                                    </Link>
                                    <Box variant="small" color="text-body-secondary">@{alias}</Box>
                                </div>
                                <RiskBadges summary={report} />
                                {knownLeaf && (
                                    <Box variant="small" color="text-status-inactive">No direct reports</Box>
                                )}
                            </SpaceBetween>
                        </div>
                    </div>
                </div>
                {isExpanded && node?.loading && (
                    <Box margin={{ left: "l", bottom: "xs" }}>
                        <StatusIndicator type="loading">Loading direct reports</StatusIndicator>
                    </Box>
                )}
                {isExpanded && node?.error && (
                    <Box margin={{ left: "l", bottom: "xs" }}>
                        <StatusIndicator type="error">{node.error}</StatusIndicator>
                    </Box>
                )}
                {isExpanded && node && !node.loading && !node.error && renderReports(node.directReports, depth + 1)}
            </div>
        );
    };

    const selectedNode = nodes[props.selectedAlias];
    const directReports = selectedNode?.directReports ?? [];
    const filteredReports = directReports.filter((r) => {
        const content = [r.managerAlias, r.managerName].join(" ").toLowerCase();
        return !filteringText || content.includes(filteringText.toLowerCase());
    });

    return (
        <Container
            header={
                <Header
                    counter={selectedManager ? `(${directReports.length})` : undefined}
                    description="Select a manager to view their org's risk."
                >
                    Organization
                </Header>
            }
        >
            <SpaceBetween size="s">
                <SpaceBetween direction="horizontal" size="xs">
                    <Input
                        type="search"
                        ariaLabel="Manager alias"
                        placeholder="Manager alias"
                        value={aliasInput}
                        onChange={(event) => setAliasInput(event.detail.value)}
                        onKeyDown={(event) => {
                            if (event.detail.key === "Enter") goToAlias(aliasInput);
                        }}
                    />
                    <Button onClick={() => goToAlias(aliasInput)}>Load</Button>
                </SpaceBetween>

                {ancestors.length > 0 && selectedManager && (
                    <BreadcrumbGroup
                        ariaLabel="Management chain"
                        items={[...ancestors, selectedManager].map((m) => ({
                            text: m.managerAlias,
                            href: `#/leadership-dashboard/${encodeURIComponent(m.managerAlias)}`,
                        }))}
                        onFollow={(event) => {
                            event.preventDefault();
                            goToAlias(event.detail.text);
                        }}
                    />
                )}

                {props.selectedLoading && (
                    <StatusIndicator type="loading">Loading organization</StatusIndicator>
                )}

                {!props.selectedLoading && selectedManager && (
                    <div
                        style={{
                            background: SELECTED_BACKGROUND,
                            border: `1px solid ${SELECTED_BORDER}`,
                            borderRadius: 8,
                            padding: "12px 16px",
                        }}
                    >
                        <SpaceBetween size="xxs">
                            <Box variant="h3" padding="n">{selectedManager.managerName}</Box>
                            <Box variant="small" color="text-body-secondary">@{selectedManager.managerAlias}</Box>
                            <RiskBadges summary={selectedManager} />
                        </SpaceBetween>
                    </div>
                )}

                {!props.selectedLoading && selectedManager && (
                    directReports.length === 0 ? (
                        <Box color="text-status-inactive">No direct reports</Box>
                    ) : (
                        <SpaceBetween size="xs">
                            {directReports.length > 5 && (
                                <TextFilter
                                    filteringPlaceholder="Filter direct reports"
                                    filteringAriaLabel="Filter direct reports"
                                    filteringText={filteringText}
                                    onChange={({ detail }) => setFilteringText(detail.filteringText)}
                                    countText={`${filteredReports.length} matches`}
                                />
                            )}
                            <div style={{ maxHeight: "calc(100vh - 360px)", minHeight: 200, overflowY: "auto" }}>
                                {renderReports(filteredReports, 0)}
                            </div>
                        </SpaceBetween>
                    )
                )}
            </SpaceBetween>
        </Container>
    );
};

export default LeadershipTree;
